import { Injectable } from '@angular/core';
import { 
  PricingDisplayProduct, 
  PricingDisplayPlan, 
  PricingDisplayFeature, 
  PricingDisplayFeatureGroup,
  PricingDisplayAddon,
  PricingDisplayRegion,
  RegionsRangedPricing
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class PricingDataTransformService {
  
  /**
   * Transform admin API product to display model.
   * Server provides product-level `features` and `featureGroups`, and each plan
   * has `enabledFeatures` entries with optional value/label/hint.
   * We build plan-level grouped/ungrouped/highlighted features according to `enabledFeatures`.
   * Additionally, we restore product-level grouped/ungrouped features enriched with cross-plan values
   * for use by the comparison grid component.
   */
  transformProductForDisplay(adminProduct: any): PricingDisplayProduct {
    /**
     * Transform admin API product to display model.
     * Combines product-level features with per-plan enabledFeatures (including hint/label)
     * and groups features using product.featureGroups.
     * Note: We store ALL regional/ranged pricing - lookup happens dynamically in components.
     */

    const adminFeatures: any[] = adminProduct.features || [];
    const adminPlans: any[] = adminProduct.plans || [];

    // Build a map of base features by code for easy lookup, preserving original index as order
    const featureByCode: Map<string, any> = new Map(
      adminFeatures.map((f: any, index: number) => [f.code, { ...f, order: index }])
    );

    // Prepare product feature groups metadata; plan-level grouping will attach features per plan
    const productGroups: PricingDisplayFeatureGroup[] = (adminProduct.featureGroups || []).map((group: any) => ({
      id: group.id,
      title: group.title,
      description: group.description,
      order: group.order || 0,
      features: []
    }));

    // Enrich product features with planValues across all plans for comparison grid
    const enrichedFeatures: PricingDisplayFeature[] = this.enrichFeaturesWithPlanValues(adminFeatures, adminPlans);

    // Build product-level grouped features using enrichedFeatures, sorted by order
    const featureGroups: PricingDisplayFeatureGroup[] = (adminProduct.featureGroups || []).map((group: any) => ({
      id: group.id,
      title: group.title,
      description: group.description,
      order: group.order || 0,
      features: enrichedFeatures
        .filter((f: any) => (featureByCode.get(f.code)?.groupId) === group.id)
        .sort((a, b) => a.order - b.order)
    }));

    // Product-level ungrouped features (no groupId), sorted by order
    const ungroupedFeatures: PricingDisplayFeature[] = enrichedFeatures
      .filter((f: any) => {
        const base = featureByCode.get(f.code);
        return !base || !base.groupId;
      })
      .sort((a, b) => a.order - b.order);

    // Transform plans and attach plan-specific grouped/ungrouped/highlighted features
    const plans: PricingDisplayPlan[] = adminPlans.map((plan: any) =>
      this.transformPlanForDisplay(plan, featureByCode, productGroups)
    );

    return {
      id: adminProduct.id,
      code: adminProduct.code,
      name: adminProduct.name,
      description: adminProduct.description,
      plans,
      featureGroups,
      ungroupedFeatures,
      addons: adminProduct.addons?.map((addon: any) => this.transformAddonForDisplay(addon)) || [],
      rangedValue: adminProduct.rangedValue,
      priceDisplayRule: adminProduct.priceDisplayRule,
      priority: adminProduct.priority,
      regions: adminProduct.regions?.map((region: any) => this.transformRegion(region)) || []
    };
  }

  /**
   * Transform a single plan, including prices and the list of features
   * enabled for this plan (resolved from the product feature map).
   */
  /**
   * Transform a single plan, including prices and the plan-specific grouping of enabled features.
   */
  private transformPlanForDisplay(
    adminPlan: any,
    featureByCode?: Map<string, any>,
    productGroups?: PricingDisplayFeatureGroup[]
  ): PricingDisplayPlan {
    const plan: PricingDisplayPlan = {
      id: adminPlan.id,
      code: adminPlan.code,
      name: adminPlan.name,
      description: adminPlan.description,
      popular: adminPlan.popular || false,
      currency: adminPlan.currency || 'USD',
      highlightedFeatures: [],
      featureGroups: [],
      ungroupedFeatures: [],
      // Store ALL regional/ranged pricing - we'll look it up dynamically in the component
      regionsRangedPricing: adminPlan.regionsRangedPricing || []
    };

    // Set base pricing (fallback) - but prefer regionsRangedPricing lookup
    if (adminPlan.monthlyPrice) {
      plan.monthlyPrice = adminPlan.monthlyPrice;
    }
    
    // Calculate yearly price with 20% discount (if monthly exists)
    if (plan.monthlyPrice) {
      plan.yearlyPrice = Math.round(plan.monthlyPrice * 12 * 0.8); // 20% discount
    }

    // Build plan groups using enabledFeatures list
    const enabledList: any[] = adminPlan.enabledFeatures || [];
    const enabledCodes = new Set<string>(enabledList.map((ef: any) => ef.code));

    if (featureByCode) {
      // Highlighted features: enabled + base.highlight, sorted by order
      plan.highlightedFeatures = Array.from(enabledCodes)
        .map((code: string) => featureByCode.get(code))
        .filter((base: any) => !!base && !!base.highlight)
        .map((base: any) => this.transformFeatures([base])[0])
        .sort((a, b) => a.order - b.order);

      // Grouped features: include enabled features per product group, sorted by order
      if (productGroups && productGroups.length > 0) {
        plan.featureGroups = productGroups.map(pg => ({
          id: pg.id,
          title: pg.title,
          description: pg.description,
          order: pg.order,
          features: Array.from(enabledCodes)
            .map(code => featureByCode.get(code))
            .filter((base: any) => !!base && base.groupId === pg.id)
            .map((base: any) => this.transformFeatures([base])[0])
            .sort((a, b) => a.order - b.order)
        })).filter(g => g.features.length > 0);
      }

      // Ungrouped features: enabled features with no groupId, sorted by order
      plan.ungroupedFeatures = Array.from(enabledCodes)
        .map(code => featureByCode.get(code))
        .filter((base: any) => !!base && !base.groupId)
        .map((base: any) => this.transformFeatures([base])[0])
        .sort((a, b) => a.order - b.order);
    }

    return plan;
  }

  /**
   * Transform base admin features to display features without plan-specific values.
   * Preserves the order property if present, otherwise uses array index.
   */
  transformFeatures(adminFeatures: any[]): PricingDisplayFeature[] {
    return adminFeatures.map((feature: any, index: number) => ({
      id: feature.id,
      code: feature.code,
      name: feature.name,
      description: feature.description,
      highlight: feature.highlight || false,
      type: feature.type || 'boolean',
      order: feature.order ?? index,
      planValues: {}
    }));
  }

  /**
   * Enrich features with planValues across all plans based on adminPlan.enabledFeatures.
   * If a label is not present but hint exists, use hint as label for display.
   */
  enrichFeaturesWithPlanValues(adminFeatures: any[], adminPlans: any[]): PricingDisplayFeature[] {
    // Add order to adminFeatures before transforming to preserve original index
    const featuresWithOrder = adminFeatures.map((f, index) => ({ ...f, order: index }));
    const baseFeatures = this.transformFeatures(featuresWithOrder);
    const featureIndex: Map<string, PricingDisplayFeature> = new Map(
      baseFeatures.map(f => [f.code, f])
    );

    for (const plan of adminPlans || []) {
      const planCode: string = plan.code;
      const enabledList: any[] = plan.enabledFeatures || [];
      const enabledByCode: Map<string, any> = new Map(enabledList.map((ef: any) => [ef.code, ef]));

      for (const feature of baseFeatures) {
        const ef = enabledByCode.get(feature.code);
        const enabled = !!ef && (ef.value === undefined ? true : !!ef.value || (typeof ef.value === 'number' && ef.value > 0));
        const value = typeof ef?.value === 'number' ? ef.value : undefined;
        // (ef?.label && ef.label !== '') ? ef.label : (ef?.hint || undefined);
        const label = ef?.label;
        const hint = ef?.hint;
        feature.planValues = feature.planValues || {};
        feature.planValues[planCode] = { enabled, value, label, hint };
      }
    }

    return baseFeatures;
  }

  /**
   * Legacy helper retained for compatibility; groups without cross-plan planValues.
   * Prefer grouping in transformProductForDisplay using enrichFeaturesWithPlanValues.
   */
  transformFeatureGroups(adminGroups: any[]): PricingDisplayFeatureGroup[] {
    return adminGroups.map((group: any) => ({
      id: group.id,
      title: group.title,
      description: group.description,
      order: group.order || 0,
      features: this.transformFeatures(group.features || [])
    }));
  }

  transformAddonForDisplay(adminAddon: any): PricingDisplayAddon {
    return {
      id: adminAddon.id,
      code: adminAddon.code,
      name: adminAddon.name,
      description: adminAddon.description,
      regionsRangedPricing: adminAddon.regionsRangedPricing || [],
      highlight: adminAddon.highlight || false,
      type: adminAddon.type || 'boolean',
      value: adminAddon.value,
      label: adminAddon.label
    };
  }

  transformRegion(adminRegion: any): PricingDisplayRegion {
    return {
      code: adminRegion.country,
      name: adminRegion.country,
      currency: adminRegion.currency,
      currencySymbol: adminRegion.currencySymbol,
      flag: adminRegion.flag,
      isDefault: adminRegion.isDefault || false
    };
  }

  calculatePriceForRegion(plan: PricingDisplayPlan, regionCode: string, rangedValue: number = 1): number | null {
    if (!plan.regionsRangedPricing) {
      return plan.monthlyPrice || null;
    }

    const pricing = plan.regionsRangedPricing.find(rp => 
      rp.country === regionCode && rp.rangedVal === rangedValue
    );

    return pricing ? pricing.price : null;
  }

  formatPrice(price: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price);
  }
}
